import { Suspense, lazy, memo } from "react";

import { AsyncDataSuspense } from "@tsp-core/async/components/async-data-suspense";

import { accountsApi } from "@tsp-domains/accounts/api";
import { contractorsAccountsApi } from "@tsp-domains/contractor-accounts/api";
import { contractsApi } from "@tsp-domains/contracts/api";
import { departmentsApi } from "@tsp-domains/departments/api";
import { notificationsApi } from "@tsp-domains/notifications/api";
import { partiesApi } from "@tsp-domains/parties/api";
import { positionsApi } from "@tsp-domains/positions/api";
import { projectsPortfolioApi } from "@tsp-domains/projects-portfolio/api";
import { soulEntitiesApi } from "@tsp-domains/soul-entities/api";
import { SoulEntityContextProvider } from "@tsp-domains/soul-entities/components/soul-entity-context";
import {
  useLoadSoulEntityPayload,
  useSoulEntityContextParamsFromURI,
  useSoulEntityContextTypeFromURI,
} from "@tsp-domains/soul-entities/components/soul-entity-context/soul-entity-context.hooks";
import { systemTypesApi } from "@tsp-domains/system-types/api";
import { workSubTypesApi, workTypesApi } from "@tsp-domains/work-types/api";

import { Access } from "../../features/auth/components";
import { Layout } from "../../features/layout";
import { UserStateData } from "../../features/user";

const Content = lazy(() => import("./work-items-listing-with-soul-entity.content"));

const checkAccess = (user: UserStateData) => user.authenticated;

function WorkItemsListingWithSoulEntityPage() {
  const getDepartmentsDictionaryQuery = departmentsApi.useGetDepartmentsDictionaryQuery();
  const getNotificationListQuery = notificationsApi.useGetNotificationListQuery();
  const getPartiesDictionaryQuery = partiesApi.useGetPartiesDictionaryQuery();
  const getPositionsDictionaryQuery = positionsApi.useGetPositionsDictionaryQuery();
  const getProjectsPortfolioQuery = projectsPortfolioApi.useGetProjectsPortfolioQuery();
  const getWorkSubTypesDictionaryQuery = workSubTypesApi.useGetWorkSubTypesDictionaryQuery();
  const getWorkTypesDictionaryQuery = workTypesApi.useGetWorkTypesDictionaryQuery();
  const loadUserContractsQuery = contractsApi.useLoadUserContractsQuery();
  const loadSystemTypesDictionaryQuery = systemTypesApi.useGetSystemTypesDictionaryQuery();
  const loadUserAccountsInfoQuery = accountsApi.useGetUserAccountsInfoQuery();
  const loadContractorsAccountsQuery = contractorsAccountsApi.useGetContractorsAccountsQuery();

  const type = useSoulEntityContextTypeFromURI();
  const params = useSoulEntityContextParamsFromURI();
  const payload = useLoadSoulEntityPayload();
  const loadSoulEntityInfoQuery = soulEntitiesApi.useLoadSoulEntityInfoQuery(payload);

  return (
    <Access checkAccess={checkAccess}>
      <Layout loading={false}>
        <AsyncDataSuspense
          queries={[
            getDepartmentsDictionaryQuery,
            getNotificationListQuery,
            getPartiesDictionaryQuery,
            getPositionsDictionaryQuery,
            getProjectsPortfolioQuery,
            getWorkSubTypesDictionaryQuery,
            getWorkTypesDictionaryQuery,
            loadSoulEntityInfoQuery,
            loadUserContractsQuery,
            loadSystemTypesDictionaryQuery,
            loadUserAccountsInfoQuery,
            loadContractorsAccountsQuery,
          ]}
        >
          <Suspense fallback={null}>
            {loadSoulEntityInfoQuery.data ? (
              <SoulEntityContextProvider
                type={type as any}
                params={params as any}
                record={loadSoulEntityInfoQuery.data as any}
              >
                <Content />
              </SoulEntityContextProvider>
            ) : null}
          </Suspense>
        </AsyncDataSuspense>
      </Layout>
    </Access>
  );
}

export default memo(WorkItemsListingWithSoulEntityPage);
